// Extracted from api/runs.ts to keep that file focused.
// Read-side artifact endpoints: workflow IR projection, versioned per-node
// artifacts (JSON), and the artifact-files area (binary/text files written
// by in-sandbox tools via $ITERION_ARTIFACT_FILES_DIR).

import { desktop, isDesktop } from "@/lib/desktopBridge";
import { downloadBlob } from "@/lib/download";
import { apiURL, extractErrorMessage, request, withStoreParam } from "./client";
import type {
  Artifact,
  ArtifactFile,
  ArtifactSummary,
  DownloadOutcome,
  PlanSnapshot,
  RunArtifactSummary,
  WireWorkflow,
} from "./types";
import {
  ASSISTANT_ACTIONS,
  parseAssistantActionRequestsDetailed,
  type RejectedAssistantAction,
  type AssistantActionRequest,
} from "@/lib/chatDock/assistantActions";
import { loadEvents } from "./snapshot";

// listAllArtifacts returns the latest published artifact per node for a
// run (node id, version, labels, title) — the data behind the centralized,
// label-grouped Artifacts view. Empty when the run published nothing.
export async function listAllArtifacts(
  runId: string,
  opts?: { signal?: AbortSignal },
): Promise<RunArtifactSummary[]> {
  const res = await request<{ artifacts: RunArtifactSummary[] }>(
    `/runs/${encodeURIComponent(runId)}/artifacts`,
    { signal: opts?.signal },
  );
  return res.artifacts ?? [];
}

export async function getRunWorkflow(runId: string, attemptId?: string): Promise<WireWorkflow> {
  const params = new URLSearchParams();
  if (attemptId) params.set("attempt", attemptId);
  const qs = withStoreParam(params).toString();
  return request(`/runs/${encodeURIComponent(runId)}/workflow${qs ? `?${qs}` : ""}`);
}

export async function listArtifacts(
  runId: string,
  nodeId: string,
  opts?: { signal?: AbortSignal },
): Promise<ArtifactSummary[]> {
  const res = await request<{ artifacts: ArtifactSummary[] }>(
    `/runs/${encodeURIComponent(runId)}/artifacts/${encodeURIComponent(nodeId)}`,
    { signal: opts?.signal },
  );
  return res.artifacts ?? [];
}

export async function getArtifact(
  runId: string,
  nodeId: string,
  version: number,
  opts?: { signal?: AbortSignal },
): Promise<Artifact> {
  return request(
    `/runs/${encodeURIComponent(runId)}/artifacts/${encodeURIComponent(nodeId)}/${version}`,
    { signal: opts?.signal },
  );
}

// listPlans returns the chronological plan snapshots captured for a run —
// the agents' TodoWrite/todo_write living TODO lists. Ascending seq order
// (how the plan evolved). Empty for older runs / cloud stores that predate
// or don't back the feature.
export async function listPlans(
  runId: string,
  opts?: { signal?: AbortSignal },
): Promise<PlanSnapshot[]> {
  const res = await request<{ plans: PlanSnapshot[] }>(
    `/runs/${encodeURIComponent(runId)}/plans`,
    { signal: opts?.signal },
  );
  return res.plans ?? [];
}

export async function listArtifactFiles(runId: string): Promise<ArtifactFile[]> {
  const res = await request<{ files: ArtifactFile[] }>(
    `/runs/${encodeURIComponent(runId)}/artifact-files`,
  );
  return res.files ?? [];
}

// Build the URL to download a single artifact file. Returns a string
// (not a fetch wrapper) because the caller hands it straight to an
// `<a href>` for browser download / new-tab preview.
export function artifactFileURL(runId: string, relPath: string): string {
  // The path can contain `/` segments; encodeURIComponent would clobber
  // them. Encode each segment individually so subdirs survive.
  const segments = relPath.split("/").map(encodeURIComponent).join("/");
  return apiURL(`/runs/${encodeURIComponent(runId)}/artifact-files/${segments}`);
}

// fetchArtifactFile downloads one artifact file body via the same
// auth-aware fetch surface as every other API call (cookies + Bearer).
// `download=true` flips the backend's Content-Disposition to
// `attachment` so previewable content types (json, md) still trigger
// a real download instead of an inline render.
export async function fetchArtifactFile(
  runId: string,
  relPath: string,
  opts: { download?: boolean } = {},
): Promise<{ blob: Blob; contentType: string }> {
  const url = artifactFileURL(runId, relPath) + (opts.download ? "?download=1" : "");
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) {
    throw new Error(`API error ${res.status}: ${await extractErrorMessage(res)}`);
  }
  return {
    blob: await res.blob(),
    contentType: res.headers.get("Content-Type") ?? "application/octet-stream",
  };
}

// downloadArtifactFile fetches the file and saves it to disk. In
// desktop mode (Wails) it routes through the SaveBinaryFile native
// binding so a real save dialog opens — the embedded WebKit silently
// swallows `<a download>` blob URLs, which is why this can't just
// rely on the DOM trick. In browser mode we fall back to the blob
// URL approach, which the user's browser handles natively.
export async function downloadArtifactFile(
  runId: string,
  relPath: string,
): Promise<DownloadOutcome> {
  const { blob, contentType } = await fetchArtifactFile(runId, relPath, { download: true });
  const basename = relPath.includes("/")
    ? relPath.slice(relPath.lastIndexOf("/") + 1)
    : relPath;

  if (isDesktop()) {
    const b64 = await blobToBase64(blob);
    const localPath = await desktop.saveBinaryFile(basename, b64);
    if (!localPath) {
      // User cancelled the native save dialog.
      return { cancelled: true, contentType };
    }
    return { cancelled: false, localPath, contentType };
  }

  downloadBlob(blob, basename, { revokeMs: 5000 });
  return { cancelled: false, contentType };
}

// blobToBase64 strips the `data:<mime>;base64,` prefix from FileReader
// output and hands back the raw payload — the Wails SaveBinaryFile
// binding decodes plain base64 (no data-URL wrapper).
async function blobToBase64(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer();
  const bytes = new Uint8Array(buf);
  // Build the binary-string in 32 KB pieces and join at the end:
  // `binary += String.fromCharCode(...)` would be O(N²) on the total
  // size (every += copies an ever-growing immutable string), freezing
  // the UI thread for several seconds on multi-MB artifact downloads.
  // Array#join allocates once over the concatenated length.
  const parts: string[] = [];
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    parts.push(String.fromCharCode(...bytes.subarray(i, i + chunk)));
  }
  return btoa(parts.join(""));
}

// A conversational bot can produce a `.bot` DRAFT as part of a turn — Copi's
// `design` posture does, and a deterministic node compiles it before the
// operator reads the reply. The draft is NOT a file: the bot is read-only with
// respect to the workspace, so it lives only in that node's artifact.
//
// Found by SHAPE, never by node name. The studio's chat registry is
// manifest-driven (issue #333), so hardcoding "copi" here would put a specific
// bot's node id back into studio code — the same coupling the registry
// removed. Any node publishing a non-empty `draft_bot` string qualifies.
export const DRAFT_BOT_FIELD = "draft_bot";
export const EDITOR_SESSION_FIELD = "editor_session_id";
export const EDITOR_REVISION_FIELD = "editor_revision";
export const EDITOR_APPLY_INTENT_FIELD = "editor_apply_intent";
export const EDITOR_SAVE_INTENT_FIELD = "editor_save_intent";
// editor_file is a HINT, never a target (#1647): for a bot in several files
// it says which file `draft_bot` holds the text of, and the offer only
// refuses to apply while the Source view's picker — the actual write
// target — names another one. Intent's spelling for this surface: the
// model asks, the author's selection decides.
export const EDITOR_FILE_FIELD = "editor_file";
export const ASSISTANT_ACTIONS_FIELD = "assistant_actions";
export const FILE_CHANGES_FIELD = "file_changes";
export const FILE_CHANGES_INTENT_FIELD = "file_changes_intent";

export type EditorActionIntent = "none" | "suggested" | "explicit";

// The bot's own posture for the turn. A conversational bot that designs
// workflows announces it here BEFORE it has anything to show, which is what
// lets the studio offer the editor first and the draft second — the order the
// operator asked for: settle where the work happens, then do it there.
export const MODE_FIELD = "mode";
export const DESIGN_MODE = "design";

export interface DraftLookup {
  /** The `.bot` source, when this conversation has produced one. */
  source: string | null;
  /** True when the latest turn is designing, draft or not. */
  designing: boolean;
}

export interface EditorProposalLookup {
  source: string | null;
  sessionId: string | null;
  revision: number | null;
  applyIntent: EditorActionIntent;
  saveIntent: EditorActionIntent;
  /** The editor_file hint: which file of a bot in several files `source`
   *  holds the text of. Null when absent — the common case for a bot in
   *  one file, where the proposal is the whole file anyway. */
  file: string | null;
}

export interface AssistantActionsLookup {
  requests: AssistantActionRequest[];
  invalid?: AssistantActionInvalidFeedback;
}

export interface AssistantActionInvalidFeedback {
  sourceKey: string;
  attempt: number;
  rejected: RejectedAssistantAction[];
  validIds: string[];
  canonicalIds: string[];
}

async function priorInvalidActionFeedbackCount(
  runId: string,
  sourceKey: string,
): Promise<number> {
  try {
    const events = await loadEvents(runId);
    return events.reduce((count, event) => {
      const answers = (event.data as { answers?: unknown } | undefined)?.answers;
      if (!answers || typeof answers !== "object" || Array.isArray(answers)) {
        return count;
      }
      const hostEvent = (answers as { host_event?: unknown }).host_event;
      if (
        hostEvent &&
        typeof hostEvent === "object" &&
        !Array.isArray(hostEvent) &&
        (hostEvent as { kind?: unknown }).kind === "assistant-action-invalid" &&
        (hostEvent as { source?: unknown }).source === sourceKey
      ) {
        return count + 1;
      }
      return count;
    }, 0);
  } catch {
    // A missing event read must not make a valid action disappear. The host
    // event endpoint remains the authority for whether feedback is accepted.
    return 0;
  }
}

export interface AssistantFileReplacement {
  before: string;
  after: string;
}

interface AssistantFileChangeTarget {
  scope: "bundle" | "workspace";
  path: string;
}

export type AssistantFileChange = AssistantFileChangeTarget & (
  | { replacements: AssistantFileReplacement[]; create?: never }
  | { create: { content: string }; replacements?: never }
);

export interface FileChangeProposalLookup {
  changes: AssistantFileChange[];
  sessionId: string | null;
  revision: number | null;
  intent: EditorActionIntent;
}

function editorActionIntent(value: unknown): EditorActionIntent {
  return value === "suggested" || value === "explicit" ? value : "none";
}

// Newest artifacts first: a draft is the LATEST thing the conversation
// produced, so the last-written node is where the search should start.
export async function lookupDraft(
  runId: string,
  opts?: { signal?: AbortSignal },
): Promise<DraftLookup> {
  const summaries = await listAllArtifacts(runId, opts);
  const ordered = [...summaries].sort((a, b) =>
    (b.written_at ?? "").localeCompare(a.written_at ?? ""),
  );
  for (const s of ordered) {
    if (opts?.signal?.aborted) return { source: null, designing: false };
    let art: Artifact;
    try {
      art = await getArtifact(runId, s.node_id, s.version, opts);
    } catch {
      // One unreadable artifact must not hide a draft published by another
      // node — keep looking rather than failing the whole lookup.
      continue;
    }
    const value = art.data?.[DRAFT_BOT_FIELD];
    // The first artifact that DECLARES a posture is the newest turn boundary.
    // An `info` turn retires an older design and its draft; a fresh design
    // cannot inherit a prior turn's draft when it has not produced one yet.
    if (
      art.data &&
      Object.prototype.hasOwnProperty.call(art.data, MODE_FIELD)
    ) {
      const designing = art.data[MODE_FIELD] === DESIGN_MODE;
      const editorSession = art.data[EDITOR_SESSION_FIELD];
      const source =
        designing &&
        !(typeof editorSession === "string" && editorSession.trim() !== "") &&
        typeof value === "string" &&
        value.trim() !== ""
          ? value
          : null;
      return { source, designing };
    }
    if (
      typeof value === "string" &&
      value.trim() !== "" &&
      !(typeof art.data?.[EDITOR_SESSION_FIELD] === "string" &&
        String(art.data[EDITOR_SESSION_FIELD]).trim() !== "")
    ) {
      // Backward compatibility for bots that publish a draft but predate
      // `mode`: a non-empty draft itself implies the design posture.
      return { source: value, designing: true };
    }
  }
  return { source: null, designing: false };
}

/**
 * Find the newest editor-bound proposal. A newer Copi artifact with an empty
 * session retires an older offer: editor capabilities are turn-scoped, never
 * sticky across an unrelated conversation reply.
 */
export async function lookupEditorProposal(
  runId: string,
  opts?: { signal?: AbortSignal },
): Promise<EditorProposalLookup> {
  const none: EditorProposalLookup = {
    source: null,
    sessionId: null,
    revision: null,
    applyIntent: "none",
    saveIntent: "none",
    file: null,
  };
  const summaries = await listAllArtifacts(runId, opts);
  const ordered = [...summaries].sort((a, b) =>
    (b.written_at ?? "").localeCompare(a.written_at ?? ""),
  );
  for (const summary of ordered) {
    if (opts?.signal?.aborted) return none;
    let artifact: Artifact;
    try {
      artifact = await getArtifact(
        runId,
        summary.node_id,
        summary.version,
        opts,
      );
    } catch {
      continue;
    }
    const data = artifact.data;
    if (!data) continue;
    const session = data[EDITOR_SESSION_FIELD];
    const revision = data[EDITOR_REVISION_FIELD];
    const source = data[DRAFT_BOT_FIELD];
    const saveIntent = editorActionIntent(data[EDITOR_SAVE_INTENT_FIELD]);
    const hasDraft = typeof source === "string" && source.trim() !== "";
    const hintedFile = data[EDITOR_FILE_FIELD];
    if (
      typeof session === "string" &&
      session.trim() !== "" &&
      typeof revision === "number" &&
      Number.isInteger(revision) &&
      revision >= 0 &&
      (hasDraft || saveIntent !== "none")
    ) {
      return {
        source: hasDraft ? source : null,
        sessionId: session,
        revision,
        // A save-only artifact can never smuggle an apply request without a
        // draft. The document to persist comes exclusively from host state.
        applyIntent: hasDraft
          ? editorActionIntent(data[EDITOR_APPLY_INTENT_FIELD])
          : "none",
        saveIntent,
        // The hint rides only a draft: a save-only turn writes the host's
        // own buffer, which no model-named file is about.
        file:
          hasDraft && typeof hintedFile === "string" && hintedFile.trim() !== ""
            ? hintedFile.trim()
            : null,
      };
    }
    // `mode` exists on every current Copi turn. Reaching it without a valid
    // proposal means this newest turn intentionally carries no editor action.
    if (Object.prototype.hasOwnProperty.call(data, MODE_FIELD)) return none;
  }
  return none;
}

/**
 * Return the newest turn's generic host-action requests. Every capable chat
 * bot publishes assistant_actions on every turn, including an empty array;
 * encountering the field therefore retires an older offer deterministically.
 */
export async function lookupAssistantActions(
  runId: string,
  opts?: { signal?: AbortSignal },
): Promise<AssistantActionsLookup> {
  const summaries = await listAllArtifacts(runId, opts);
  const ordered = [...summaries].sort((a, b) =>
    (b.written_at ?? "").localeCompare(a.written_at ?? ""),
  );
  for (const summary of ordered) {
    if (opts?.signal?.aborted) return { requests: [] };
    let artifact: Artifact;
    try {
      artifact = await getArtifact(
        runId,
        summary.node_id,
        summary.version,
        opts,
      );
    } catch {
      continue;
    }
    if (
      artifact.data &&
      Object.prototype.hasOwnProperty.call(
        artifact.data,
        ASSISTANT_ACTIONS_FIELD,
      )
    ) {
      const key = `${runId}:${summary.node_id}:${summary.version}`;
      const parsed = parseAssistantActionRequestsDetailed(
        artifact.data[ASSISTANT_ACTIONS_FIELD],
        key,
      );
      if (parsed.rejected.length > 0) {
        const prior = await priorInvalidActionFeedbackCount(runId, key);
        return {
          requests: [],
          invalid: {
            sourceKey: key,
            attempt: Math.min(prior + 1, 2),
            rejected: parsed.rejected,
            validIds: parsed.requests.map((request) => request.id),
            canonicalIds: ASSISTANT_ACTIONS.map((action) => action.id),
          },
        };
      }
      return {
        requests: parsed.requests,
      };
    }
  }
  return { requests: [] };
}

function parseFileChanges(value: unknown): AssistantFileChange[] {
  if (!Array.isArray(value) || value.length > 8) return [];
  let total = 0;
  const parsed: AssistantFileChange[] = [];
  for (const raw of value) {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return [];
    const item = raw as Record<string, unknown>;
    if (
      (item.scope !== "bundle" && item.scope !== "workspace") ||
      typeof item.path !== "string" ||
      item.path.trim() === ""
    ) {
      return [];
    }
    const hasCreate = Object.prototype.hasOwnProperty.call(item, "create");
    const hasReplacements = Object.prototype.hasOwnProperty.call(item, "replacements");
    if (hasCreate === hasReplacements) return [];
    if (hasCreate) {
      if (!item.create || typeof item.create !== "object" || Array.isArray(item.create)) return [];
      const create = item.create as Record<string, unknown>;
      if (typeof create.content !== "string" || create.content.length > 32 * 1024) return [];
      total += create.content.length;
      parsed.push({ scope: item.scope, path: item.path, create: { content: create.content } });
      continue;
    }
    if (
      !Array.isArray(item.replacements) ||
      item.replacements.length === 0 ||
      item.replacements.length > 16
    ) {
      return [];
    }
    const replacements: AssistantFileReplacement[] = [];
    for (const candidate of item.replacements) {
      if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) return [];
      const replacement = candidate as Record<string, unknown>;
      if (
        typeof replacement.before !== "string" ||
        replacement.before === "" ||
        replacement.before.length > 32 * 1024 ||
        typeof replacement.after !== "string" ||
        replacement.after.length > 32 * 1024
      ) {
        return [];
      }
      total += replacement.before.length + replacement.after.length;
      replacements.push({ before: replacement.before, after: replacement.after });
    }
    parsed.push({ scope: item.scope, path: item.path, replacements });
  }
  return total <= 256 * 1024 ? parsed : [];
}

/** Newest companion-file proposal, bound to the same editor turn snapshot. */
export async function lookupFileChangeProposal(
  runId: string,
  opts?: { signal?: AbortSignal },
): Promise<FileChangeProposalLookup> {
  const none: FileChangeProposalLookup = {
    changes: [],
    sessionId: null,
    revision: null,
    intent: "none",
  };
  const summaries = await listAllArtifacts(runId, opts);
  const ordered = [...summaries].sort((a, b) =>
    (b.written_at ?? "").localeCompare(a.written_at ?? ""),
  );
  for (const summary of ordered) {
    if (opts?.signal?.aborted) return none;
    let artifact: Artifact;
    try {
      artifact = await getArtifact(runId, summary.node_id, summary.version, opts);
    } catch {
      continue;
    }
    const data = artifact.data;
    if (!data) continue;
    const raw = data[FILE_CHANGES_FIELD];
    if (raw !== undefined) {
      const changes = parseFileChanges(raw);
      const session = data[EDITOR_SESSION_FIELD];
      const revision = data[EDITOR_REVISION_FIELD];
      if (
        changes.length > 0 &&
        typeof session === "string" &&
        session.trim() !== "" &&
        typeof revision === "number" &&
        Number.isInteger(revision) &&
        revision >= 0
      ) {
        return {
          changes,
          sessionId: session,
          revision,
          intent: editorActionIntent(data[FILE_CHANGES_INTENT_FIELD]),
        };
      }
      return none;
    }
    if (Object.prototype.hasOwnProperty.call(data, MODE_FIELD)) return none;
  }
  return none;
}

/** Back-compat shorthand for callers that only want the source. */
export async function findDraftBotSource(
  runId: string,
  opts?: { signal?: AbortSignal },
): Promise<string | null> {
  return (await lookupDraft(runId, opts)).source;
}
